import { useEffect, useState, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import "./styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

const LoginPage = () => {
  const [emailFromInput, setEmailFromInput] = useState("");
  const [passwordFromInput, setPasswordFromInput] = useState("");
  const [showCheckYourEmailMessage, setShowCheckYourEmailMessage] =
    useState(false);
  const [userFoundButHasNotVerifiedEmail, setUserFoundButHasNotVerifiedEmail] =
    useState(false);
  const [userNotFound, setUserNotFound] = useState(false);
  const [userIsFoundButPasswordIsWrong, setUserIsFoundButPasswordIsWrong] =
    useState(false);
  const [
    userIsFoundButIsNotVerifiedAndPasswordIsWrong,
    setUserIsFoundButIsNotVerifiedAndPasswordIsWrong,
  ] = useState(false);
  const [somethingElseIsWrong, setSomethingElseIsWrong] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorLoader, setErrorLoader] = useState(false);

  const { state } = useLocation();
  useEffect(() => {
    if (state) {
      setShowCheckYourEmailMessage(state.verifyEmail);
    }
  }, [state]);

  const scrollHere = useRef(null);

  const loginUrl = process.env.REACT_APP_LOGIN_URL;
  const token = window.localStorage.getItem("token");

  let navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_VERIFY_TOKEN_URL, {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          navigate("/dashboard");
        } else {
          console.log("Token is not valid or not provided");
          localStorage.removeItem("token");
        }
      } catch (error) {
        console.log("Error verifying token:", error);
      }
    };

    if (token) {
      verifyToken();
    }
  }, []);

  const bodyData = {
    email: emailFromInput,
    password: passwordFromInput,
  };

  let loginFetch = async () => {
    try {
      setLoading(true);
      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });
      if (response.status === 201) {
        const data = await response.json();
        let accessToken = data.accessToken;
        localStorage.setItem("token", JSON.stringify(accessToken));
        setLoading(false);
        navigate("/dashboard");
      } else if (response.status === 401) {
        setUserFoundButHasNotVerifiedEmail(true);
        setTimeout(() => {
          /* BELOW IS FOR DIRECTING DIRECTLY TO STRIPE  */
          window.location.assign(
            process.env.REACT_APP_STRIPE_REGISTER_PAYMENT_LINK +
              "?prefilled_email=" +
              emailFromInput,
            { state: { verifyEmail: true } }
          );
          /*            window.location.assign("/"); */
        }, "3000");
      } else if (response.status === 204) {
        console.log("User not found.");
        setLoading(false);
        setUserNotFound(true);
      } else if (response.status === 203) {
        console.log("User is verified, but password is wrong");
        setLoading(false);
        setUserIsFoundButPasswordIsWrong(true);
      } else if (response.status === 206) {
        console.log("User is not verified and password is wrong");
        setLoading(false);
        setUserIsFoundButIsNotVerifiedAndPasswordIsWrong(true);
      } else if (response.status === 400) {
        console.log("Something is wrong");
        setLoading(false);
        setSomethingElseIsWrong(true);
      }
    } catch (error) {
      console.log("error: ", error);
      console.log("something went wrong.");
      setLoading(false);
      setErrorLoader(true);
      setSomethingElseIsWrong(true);
    }
  };

  const onSubmitFunction = (event) => {
    event.preventDefault();
    loginFetch();
  };

  useEffect(() => {
    scrollHere?.current?.scrollIntoView({ behavior: "smooth" });
  });

  function renderAlert(message, type = "success", onClose = null) {
    let bgColor;

    switch (type) {
      case "warning":
        bgColor = "bg-yellow-50";
        break;
      case "error":
        bgColor = "bg-red-50";
        break;
      default:
        bgColor = "bg-green-50";
        break;
    }

    return (
      <div className={`${bgColor} rounded-md p-4 mb-4`}>
        <div className="flex">
          <div className="flex-shrink-0"></div>
          <div className="ml-3">
            <p className="text-sm font-medium">{message}</p>
          </div>
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className="inline-flex rounded-md p-1.5 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
                onClick={onClose}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center bg-gray-200 px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        {/* Alerts Section Begin */}
        <div className="space-y-4 mb-5">
          {showCheckYourEmailMessage &&
            renderAlert(
              "Check your email for further instructions.",
              "success",
              () => setShowCheckYourEmailMessage(false)
            )}
          {userFoundButHasNotVerifiedEmail &&
            renderAlert(
              "Please complete your free trial sign-up to kick off your 7-day free experience with SerpMaze.",
              "warning",
              () => setUserFoundButHasNotVerifiedEmail(false)
            )}
          {userNotFound &&
            renderAlert(
              "User not found. Please check your credentials and try again.",
              "error",
              () => setUserNotFound(false)
            )}
          {userIsFoundButPasswordIsWrong &&
            renderAlert(
              "User found, but password is wrong. Please try again.",
              "warning",
              () => setUserIsFoundButPasswordIsWrong(false)
            )}
          {userIsFoundButIsNotVerifiedAndPasswordIsWrong &&
            ((
              <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded mb-4">
                <p>
                  User found, however user has not finished the signup process
                  and password is wrong. Please start by finishing the signup
                  process via our Stripe link
                  <span
                    className="underline cursor-pointer text-green-600"
                    onClick={() =>
                      window.location.assign(
                        process.env.REACT_APP_STRIPE_REGISTER_PAYMENT_LINK +
                          "?prefilled_email=" +
                          emailFromInput,
                        { state: { verifyEmail: true } }
                      )
                    }
                  >
                    {" "}
                    here
                  </span>
                  . Once that is done, then login again using the correct
                  password.
                </p>
              </div>
            ),
            () => setUserIsFoundButIsNotVerifiedAndPasswordIsWrong(false))}
          {somethingElseIsWrong &&
            renderAlert(
              "Something is wrong. You might have entered your details incorrectly. Please write us at contact@serpmaze.com if you believe you are doing everything correctly.",
              "warning",
              () => setSomethingElseIsWrong(false)
            )}
        </div>
        {/* Alerts Section End */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-black">
            Sign in to your account
          </h2>

          <div className="mt-10 bg-white p-6 rounded shadow-md">
            <form className="space-y-4" onSubmit={onSubmitFunction}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-600"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={emailFromInput}
                  onChange={(e) => setEmailFromInput(e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div className="flex justify-between items-center">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-600"
                >
                  Password
                </label>
                <Link
                  to="/new-password"
                  className="text-indigo-600 hover:text-indigo-500 text-sm"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={passwordFromInput}
                onChange={(e) => setPasswordFromInput(e.target.value)}
                className="w-full px-3 py-2 border rounded mt-2"
              />

              <div className="mt-4">
                <button
                  type="submit"
                  className="w-full bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                  disabled={loading} // disable button while loading
                >
                  {loading ? "Loading..." : "Sign in"}
                </button>
              </div>
            </form>

            <p className="text-center text-sm mt-4">
              Not a member?
              <Link
                to="/signup"
                className="ml-2 text-indigo-600 hover:text-indigo-500"
              >
                Sign up now for free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
